import { juntarClasses } from './classes';
import { useContadorAnimado } from './movimento';

/**
 * Valores fiscais.
 *
 * Duas decisões que vêm de bug real do legado:
 *
 * 1. **Escala do percentual é explícita.** `percentSped` era gravado como fração
 *    (0–1) e `percentBeneficio` como percentual (0–100), ambos `number` cru, e
 *    dois geradores de e-mail liam o mesmo campo de formas diferentes — dado
 *    histórico saía 100× maior (RN-078). Aqui não existe um componente genérico
 *    de percentual: `ValorPercentual` recebe a prop `percentual` (0–100) e ponto.
 *    O nome da prop é o contrato de escala, como o tipo `Percentual` no domínio.
 *
 * 2. **Ausência de valor não vira zero.** Campo vazio mostra travessão, não
 *    `R$ 0,00`. Zero é uma informação fiscal; "não informado" é outra.
 */

const CACHE_FORMATADORES = new Map<string, Intl.NumberFormat>();

function formatador(opcoes: Intl.NumberFormatOptions): Intl.NumberFormat {
  const chave = JSON.stringify(opcoes);
  const existente = CACHE_FORMATADORES.get(chave);
  if (existente !== undefined) return existente;
  // `Intl.NumberFormat` é caro de construir e uma tabela chama isto por célula.
  const novo = new Intl.NumberFormat('pt-BR', opcoes);
  CACHE_FORMATADORES.set(chave, novo);
  return novo;
}

/**
 * Zero negativo é zero.
 *
 * Um mês sem crédito produz `-0` quando o cálculo multiplica por uma apuração
 * negativa, e o `Intl` formata isso como "−R$ 0,00" — um sinal de menos diante
 * de nada, que num relatório fiscal parece erro de conta. Somar zero normaliza.
 */
function semZeroNegativo(valor: number): number {
  return valor + 0;
}

export function formatarMoeda(valor: number, casas = 2): string {
  valor = semZeroNegativo(valor);
  return formatador({
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  }).format(valor);
}

export function formatarNumero(valor: number, casas = 2): string {
  valor = semZeroNegativo(valor);
  return formatador({
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  }).format(valor);
}

/** `percentual` na escala 0–100 (25 ⇒ "25,00%"). Nunca fração. */
export function formatarPercentual(percentual: number, casas = 2): string {
  return formatador({
    style: 'percent',
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  }).format(percentual / 100);
}

export type VarianteValor = 'padrao' | 'economia' | 'debito' | 'sutil';
export type TamanhoValor = 'pequeno' | 'medio' | 'grande' | 'destaque';

const VARIANTES: Readonly<Record<VarianteValor, string>> = {
  padrao: 'text-conteudo-primario',
  /** Verde só aqui: §8 reserva o verde para economia/benefício. */
  economia: 'text-economia',
  debito: 'text-erro',
  sutil: 'text-conteudo-secundario',
};

const TAMANHOS: Readonly<Record<TamanhoValor, string>> = {
  pequeno: 'text-xs',
  medio: 'text-sm',
  grande: 'text-lg font-medium',
  destaque: 'font-display text-3xl font-semibold tracking-tight',
};

interface PropsBase {
  variante?: VarianteValor;
  tamanho?: TamanhoValor;
  /** Casas decimais. Moeda em 2; deságio às vezes pede 4. */
  casas?: number;
  /** Mostra `+` em valores positivos — útil em variação. */
  comSinal?: boolean;
  /** Texto quando o valor é nulo. */
  textoSemValor?: string;
  /** Anima do valor anterior até o atual (do zero, na montagem). */
  animado?: boolean;
  className?: string;
}

export interface PropsValorFiscal extends PropsBase {
  /** `null`/`undefined` ⇒ travessão. Não vira zero. */
  valor: number | null | undefined;
  /** `false` formata número puro, sem `R$` (quantidade, meses, índice). */
  moeda?: boolean;
}

function SemValor({ texto, className }: { texto: string; className?: string }): JSX.Element {
  return (
    <span className={juntarClasses('valor-fiscal text-conteudo-secundario', className)}>
      <span aria-hidden="true">{texto}</span>
      <span className="sr-only">sem valor informado</span>
    </span>
  );
}

export function ValorFiscal({
  valor,
  moeda = true,
  variante = 'padrao',
  tamanho = 'medio',
  casas = 2,
  comSinal = false,
  textoSemValor = '—',
  animado = false,
  className,
}: PropsValorFiscal): JSX.Element {
  const finito = typeof valor === 'number' && Number.isFinite(valor);
  // O hook roda sempre (regra dos hooks); com valor ausente ele recebe 0 e o
  // resultado é descartado logo abaixo.
  const animadoAtual = useContadorAnimado(finito ? valor : 0, {
    ativo: animado,
    desdeZero: animado,
  });

  if (!finito) return <SemValor texto={textoSemValor} className={className} />;

  const exibir = animado ? animadoAtual : valor;
  const formatar = (numero: number): string =>
    moeda ? formatarMoeda(numero, casas) : formatarNumero(numero, casas);
  const prefixoDeSinal = comSinal && valor > 0 ? '+' : '';
  const textoFinal = `${prefixoDeSinal}${formatar(valor)}`;

  return (
    <span
      className={juntarClasses('valor-fiscal', VARIANTES[variante], TAMANHOS[tamanho], className)}
    >
      {animado ? (
        <>
          {/* O leitor de tela recebe o valor final uma vez, não cada quadro. */}
          <span aria-hidden="true">
            {prefixoDeSinal}
            {formatar(exibir)}
          </span>
          <span className="sr-only">{textoFinal}</span>
        </>
      ) : (
        textoFinal
      )}
    </span>
  );
}

export interface PropsValorPercentual extends Omit<PropsBase, 'comSinal'> {
  /** Escala 0–100. `25` ⇒ "25,00%". Fração (0,25) aqui vira "0,25%". */
  percentual: number | null | undefined;
}

export function ValorPercentual({
  percentual,
  variante = 'padrao',
  tamanho = 'medio',
  casas = 2,
  textoSemValor = '—',
  animado = false,
  className,
}: PropsValorPercentual): JSX.Element {
  const finito = typeof percentual === 'number' && Number.isFinite(percentual);
  const animadoAtual = useContadorAnimado(finito ? percentual : 0, {
    ativo: animado,
    desdeZero: animado,
  });

  if (!finito) return <SemValor texto={textoSemValor} className={className} />;

  const textoFinal = formatarPercentual(percentual, casas);

  return (
    <span
      className={juntarClasses('valor-fiscal', VARIANTES[variante], TAMANHOS[tamanho], className)}
    >
      {animado ? (
        <>
          <span aria-hidden="true">{formatarPercentual(animadoAtual, casas)}</span>
          <span className="sr-only">{textoFinal}</span>
        </>
      ) : (
        textoFinal
      )}
    </span>
  );
}
